import { NegativeButton, SecondaryButton, Typography } from '@musinsa/layer-components-desktop'
import { Modal } from '@musinsa/mwms-ds'
import styled, { createGlobalStyle } from 'styled-components'
import { SingleBatchSettingItem } from '@/services/outbound/singleBatchSetting/types'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'

interface Props {
  rows: SingleBatchSettingItem[]
  isPending?: boolean
  onClose: () => void
  onConfirm: () => void
}

function SingleBatchSettingDeleteConfirmDialog({ rows, isPending, onClose, onConfirm }: Props) {
  const firstRow = rows[0]
  const { options: warehouseOptions } = useGetCutOffWarehouses()
  const firstWarehouseName = warehouseOptions.find((o) => o.value === firstRow.warehouseCode)?.label ?? firstRow.warehouseCode ?? ''

  // 명단이 있는 조건(바코드·SKU ID). barcodes 는 서버가 최대 20건만 주는 미리보기라 건수는 barcodeCount 로 센다
  const listRows = rows.filter((r) => r.conditionType !== 'CENTER_ALL')
  const hasAllProduct = listRows.length < rows.length
  const hasListProduct = listRows.length > 0
  const firstItem = listRows[0]?.barcodes[0] ?? ''
  const totalItemCount = listRows.reduce((sum, row) => sum + (row.barcodeCount ?? row.barcodes.length), 0)

  const productNode = (() => {
    if (hasAllProduct && !hasListProduct) return <>전체상품</>
    if (hasAllProduct && hasListProduct) return <>{firstItem} 외 다수상품</>
    if (totalItemCount <= 1) return <>{firstItem}</>
    return <>{firstItem} 외 <OrangeSpan>{(totalItemCount - 1).toLocaleString()}개</OrangeSpan></>
  })()

  return (
    <Modal open customWidth={400} hideFooter contentClassName="delete-confirm-dialog-content" onClose={onClose}>
      <DeleteConfirmGlobalStyle />
      <DialogBody>
        <ContentRow>
          <IconWrapper>
            <AlertTriangleIcon />
          </IconWrapper>
          <TextContent>
            <TitleText>
              {firstWarehouseName}
              {rows.length > 1 && <> 외 <OrangeSpan>{rows.length - 1}</OrangeSpan></>}
              {' / '}
              {productNode}
              <br />
              단수합포 설정을 삭제하시겠습니까?
            </TitleText>
            <Typography name="base-text-l" colorToken="fg">
              삭제된 설정은 복구할 수 없습니다
            </Typography>
          </TextContent>
        </ContentRow>
      </DialogBody>

      <DialogFooter>
        <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="취소" onClick={onClose} />
        <NegativeButton variant="outlined" size="medium" widthMode="compact" label="삭제" disabled={isPending} onClick={onConfirm} />
      </DialogFooter>
    </Modal>
  )
}

function AlertTriangleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill="#fa9200" />
      <line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="white" />
    </svg>
  )
}

const DeleteConfirmGlobalStyle = createGlobalStyle`
  .delete-confirm-dialog-content {
    padding: 0 !important;
  }
`

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 24px 0;
`

const ContentRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const TitleText = styled.p`
  font-family: 'Pretendard', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.36;
  color: var(--fg, black);
  margin: 0;
  white-space: pre-wrap;
`

const OrangeSpan = styled.span`
  color: #fa9200;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
`

export default SingleBatchSettingDeleteConfirmDialog
